import { validateArgentinaId } from "../lib/validate-argentine-id";
import { validateItalianId } from "../lib/validate-italian-id";
import { validateSpanishId } from "../lib/validate-spanish-id";
import { validateUKId } from "../lib/validate-uk-id";

export interface RegionMetadata {
  countryCode: string; // Código ISO de 2 letras (ES, MX, AR...)
  countryName: string; // Nombre de visualización interno
  platformBillingCurrency: string; // Divisa en la que Fysaro cobra
  defaultBusinessCurrency: string; // Divisa por defecto de la clinica
  locale: string; // Configuración regional para formatos de fecha/idioma
  timeZone: string; // Zona IANA por defecto para la operativa de la clínica
  taxName: string; // Nombre de impuesto Ej IVA
  taxDefault: number; // IVA por defecto Ej 21
  allowedTaxRates: number[]; // Distintos tipos de IVA [0, 4, 21]
  allowedLanguages: string[]; // Idiomas soportados por la clinica ["es", "en"]
  taxIdType: string; // Clave para el sistema de traducción (ej: "regions.tax_id.es")
  placeholderIdType: string; // Clave para el placeholder traducido
  validationRegex: RegExp; // El filtro de formato básico para el documento
  validateFunction?: (value: string) => boolean; // Algoritmos complejos específicos
}

export const REGIONAL_DICTIONARY: Record<string, RegionMetadata> = {
  ES: {
    countryCode: "ES",
    countryName: "España",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "es-ES",
    timeZone: "Europe/Madrid",
    taxName: "IVA",
    taxDefault: 21,
    allowedTaxRates: [0, 4, 10, 21], // Exento, Superreducido, Reducido, General
    allowedLanguages: ["es", "en"],
    taxIdType: "tax_id.es", // Renderizará "DNI / NIE"
    placeholderIdType: "placeholder.es", // Renderizará "Ej: 12345678Z"
    validationRegex:
      /^([0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]|[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]|[ABCDEFGHJNPQRSTUVW][0-9]{7}[A-Z0-9])$/i,
    validateFunction: validateSpanishId,
  },
  IT: {
    countryCode: "IT",
    countryName: "Italia",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR", // Comparte moneda pero cambia el formato y los textos fiscales
    locale: "it-IT",
    timeZone: "Europe/Rome",
    taxName: "IVA",
    taxDefault: 22,
    allowedTaxRates: [0, 4, 10, 22],
    allowedLanguages: ["it", "en"],
    taxIdType: "tax_id.it", // Renderizará "Codice Fiscale / Partita IVA"
    placeholderIdType: "placeholder.it",
    // Regex estándar para Codice Fiscale (16 caracteres alfanuméricos) o Partita IVA (11 dígitos)
    validationRegex:
      /^(?:[A-Z]{6}\d{2}[A-EHLMPRSTZ]\d{2}[A-Z\d]{4}[A-Z]|\d{11})$/i,
    validateFunction: validateItalianId,
  },
  FR: {
    countryCode: "FR",
    countryName: "France",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "fr-FR",
    timeZone: "Europe/Paris",
    taxName: "TVA",
    taxDefault: 20,
    allowedTaxRates: [0, 5.5, 10, 20],
    allowedLanguages: ["fr", "en"], 
    taxIdType: "tax_id.fr",
    placeholderIdType: "placeholder.fr",
    validationRegex: /^[0-9]{9}$/, // SIREN (9 dígitos)
  },
  DE: {
    countryCode: "DE",
    countryName: "Deutschland",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "de-DE",
    timeZone: "Europe/Berlin",
    taxName: "USt",
    taxDefault: 19,
    allowedTaxRates: [0, 7, 19],
    allowedLanguages: ["de", "en"],
    taxIdType: "tax_id.de",
    placeholderIdType: "placeholder.de",
    validationRegex: /^DE[0-9]{9}$/i, // VAT alemán
  },
  PT: {
    countryCode: "PT",
    countryName: "Portugal",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "pt-PT",
    timeZone: "Europe/Lisbon",
    taxName: "IVA",
    taxDefault: 23,
    allowedTaxRates: [0, 6, 13, 23],
    allowedLanguages: ["pt", "en", "es"],
    taxIdType: "tax_id.pt",
    placeholderIdType: "placeholder.pt",
    validationRegex: /^[0-9]{9}$/, // NIF
  },
  NL: {
    countryCode: "NL",
    countryName: "Netherlands",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "nl-NL",
    timeZone: "Europe/Amsterdam",
    taxName: "BTW",
    taxDefault: 21,
    allowedTaxRates: [0, 9, 21],
    allowedLanguages: ["nl", "en"],
    taxIdType: "tax_id.nl",
    placeholderIdType: "placeholder.nl",
    validationRegex: /^[A-Z0-9]{12}$/, // BTW-nummer simplificado
  },
  BE: {
    countryCode: "BE",
    countryName: "Belgium",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "fr-BE",
    timeZone: "Europe/Brussels",
    taxName: "TVA",
    taxDefault: 21,
    allowedTaxRates: [0, 6, 12, 21],
    allowedLanguages: ["fr", "nl", "en"],
    taxIdType: "tax_id.be",
    placeholderIdType: "placeholder.be",
    validationRegex: /^[0-9]{10}$/, // Número TVA
  },
  IE: {
    countryCode: "IE",
    countryName: "Ireland",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "en-IE",
    timeZone: "Europe/Dublin",
    taxName: "VAT",
    taxDefault: 23,
    allowedTaxRates: [0, 9, 13.5, 23],
    allowedLanguages: ["en"],
    taxIdType: "tax_id.ie",
    placeholderIdType: "placeholder.ie",
    validationRegex: /^[0-9A-Z]{8,9}$/, // VAT irlandés
  },
  SE: {
    countryCode: "SE",
    countryName: "Sweden",
    platformBillingCurrency: "SEK",
    defaultBusinessCurrency: "SEK",
    locale: "sv-SE",
    timeZone: "Europe/Stockholm",
    taxName: "MOMS",
    taxDefault: 25,
    allowedTaxRates: [0, 6, 12, 25],
    allowedLanguages: ["sv", "en"],
    taxIdType: "tax_id.se",
    placeholderIdType: "placeholder.se",
    validationRegex: /^[0-9]{12}$/, // Org.nr
  },
  NO: {
    countryCode: "NO",
    countryName: "Norway",
    platformBillingCurrency: "NOK",
    defaultBusinessCurrency: "NOK",
    locale: "nb-NO",
    timeZone: "Europe/Oslo",
    taxName: "MVA",
    taxDefault: 25,
    allowedTaxRates: [0, 12, 15, 25],
    allowedLanguages: ["no", "en"],
    taxIdType: "tax_id.no",
    placeholderIdType: "placeholder.no",
    validationRegex: /^[0-9]{9}$/, // Org.nr
  },
  DK: {
    countryCode: "DK",
    countryName: "Denmark",
    platformBillingCurrency: "DKK",
    defaultBusinessCurrency: "DKK",
    locale: "da-DK",
    timeZone: "Europe/Copenhagen",
    taxName: "MOMS",
    taxDefault: 25,
    allowedTaxRates: [0, 25],
    allowedLanguages: ["da", "en"],
    taxIdType: "tax_id.dk",
    placeholderIdType: "placeholder.dk",
    validationRegex: /^[0-9]{8}$/, // CVR
  },
  BG: {
    countryCode: "BG",
    countryName: "Bulgaria",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "bg-BG",
    timeZone: "Europe/Sofia",
    taxName: "DDS",
    taxDefault: 20,
    allowedTaxRates: [0, 9, 20],
    allowedLanguages: ["bg", "en"],
    taxIdType: "tax_id.bg",
    placeholderIdType: "placeholder.bg",
    validationRegex: /^[0-9]{9}$/, // EIK/BULSTAT
  },
  FI: {
    countryCode: "FI",
    countryName: "Finland",
    platformBillingCurrency: "EUR",
    defaultBusinessCurrency: "EUR",
    locale: "fi-FI",
    timeZone: "Europe/Helsinki",
    taxName: "ALV",
    taxDefault: 24,
    allowedTaxRates: [0, 10, 14, 24],
    allowedLanguages: ["fi", "sv", "en"],
    taxIdType: "tax_id.fi",
    placeholderIdType: "placeholder.fi",
    validationRegex: /^[0-9]{8}$/, // Y-tunnus
  },
  GB: {
    countryCode: "GB",
    countryName: "United Kingdom",
    platformBillingCurrency: "GBP",
    defaultBusinessCurrency: "GBP", // 💷 Su propia moneda: Libra Esterlina
    locale: "en-GB", // Formato de fecha dd/mm/yyyy igual que España, pero en inglés
    timeZone: "Europe/London",
    taxName: "VAT", // 🌟 El IVA británico se llama VAT
    taxDefault: 20,
    allowedTaxRates: [0, 5, 20],
    allowedLanguages: ["en"],
    taxIdType: "tax_id.gb", // Renderizará "Tax ID / Registration Number" o "CRN / VRN"
    placeholderIdType: "placeholder.global",
    // Formato típico de registro de empresas (CRN - 8 dígitos) o VAT number (9 dígitos)
    validationRegex: /^(?:\d{8}|GB\d{9})$/i,
    validateFunction: validateUKId,
  },
  ///////////////////// LATAM //////////////////////
  CL: {
    countryCode: "CL",
    countryName: "Chile",
    platformBillingCurrency: "CLP",
    defaultBusinessCurrency: "CLP",
    locale: "es-CL",
    timeZone: "America/Santiago",
    taxName: "IVA",
    taxDefault: 19,
    allowedTaxRates: [0, 19],
    allowedLanguages: ["es"],
    taxIdType: "tax_id.cl",
    placeholderIdType: "placeholder.cl",
    validationRegex: /^[0-9]{1,2}\.[0-9]{3}\.[0-9]{3}-[0-9Kk]$/, // RUT
  },
  CO: {
    countryCode: "CO",
    countryName: "Colombia",
    platformBillingCurrency: "COP",
    defaultBusinessCurrency: "COP",
    locale: "es-CO",
    timeZone: "America/Bogota",
    taxName: "IVA",
    taxDefault: 0, // La salud suele estar excluida/exenta (0%), o 19% general
    allowedTaxRates: [0, 5, 19],    // Exento, Reducido, General de Colombia
    allowedLanguages: ["es"],
    taxIdType: "tax_id.co",
    placeholderIdType: "placeholder.co",
    validationRegex: /^[0-9]{9,10}$/, // NIT
  },
  PE: {
    countryCode: "PE",
    countryName: "Perú",
    platformBillingCurrency: "PEN",
    defaultBusinessCurrency: "PEN",
    locale: "es-PE",
    timeZone: "America/Lima",
    taxName: "IGV",
    taxDefault: 18,
    allowedTaxRates: [0, 18],
    allowedLanguages: ["es"],
    taxIdType: "tax_id.pe",
    placeholderIdType: "placeholder.pe",
    validationRegex: /^[0-9]{11}$/, // RUC
  },
  UY: {
    countryCode: "UY",
    countryName: "Uruguay",
    platformBillingCurrency: "UYU",
    defaultBusinessCurrency: "UYU",
    locale: "es-UY",
    timeZone: "America/Montevideo",
    taxName: "IVA",
    taxDefault: 22,
    allowedTaxRates: [0, 10, 22],
    allowedLanguages: ["es"],
    taxIdType: "tax_id.uy",
    placeholderIdType: "placeholder.uy",
    validationRegex: /^[0-9]{12}$/, // RUT
  },
  EC: {
    countryCode: "EC",
    countryName: "Ecuador",
    platformBillingCurrency: "USD",
    defaultBusinessCurrency: "USD",
    locale: "es-EC",
    timeZone: "America/Guayaquil",
    taxName: "IVA",
    taxDefault: 15,
    allowedTaxRates: [0, 15],
    allowedLanguages: ["es"],
    taxIdType: "tax_id.ec",
    placeholderIdType: "placeholder.ec",
    validationRegex: /^[0-9]{13}$/, // RUC
  },
  MX: {
    countryCode: "MX",
    countryName: "México",
    platformBillingCurrency: "MXN",
    defaultBusinessCurrency: "MXN",
    locale: "es-MX",
    timeZone: "America/Mexico_City",
    taxName: "IVA",
    taxDefault: 16,
    allowedTaxRates: [0, 8, 16],
    allowedLanguages: ["es"],
    taxIdType: "tax_id.mx", // Renderizará "RFC / CURP"
    placeholderIdType: "placeholder.mx",
    validationRegex: /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/,
  },
  AR: {
    countryCode: "AR",
    countryName: "Argentina",
    platformBillingCurrency: "USD",
    defaultBusinessCurrency: "ARS",
    locale: "es-AR",
    timeZone: "America/Argentina/Buenos_Aires",
    taxName: "IVA",
    taxDefault: 21,
    allowedTaxRates: [0, 10.5, 21],
    allowedLanguages: ["es"],
    taxIdType: "tax_id.ar", // Renderizará "DNI / CUIT / CUIL"
    placeholderIdType: "placeholder.ar",
    validationRegex: /^[0-9\s\-]{7,11}$/,
    validateFunction: validateArgentinaId,
  },
  BR: {
    countryCode: "BR",
    countryName: "Brazil",
    platformBillingCurrency: "BRL",
    defaultBusinessCurrency: "BRL",
    locale: "pt-BR",
    timeZone: "America/Sao_Paulo",
    taxName: "ICMS",
    taxDefault: 18,
    allowedTaxRates: [0, 5, 12, 18],
    allowedLanguages: ["pt"],
    taxIdType: "tax_id.br",
    placeholderIdType: "placeholder.br",
    validationRegex: /^[0-9]{11}$/, // CPF básico
  },
  // Comodín elástico para países no mapeados de forma estricta todavía
  DEFAULT: {
    countryCode: "GLOBAL",
    countryName: "Internacional",
    platformBillingCurrency: "USD",
    defaultBusinessCurrency: "USD",
    locale: "en-US",
    timeZone: "UTC",
    taxName: "TAX",
    taxDefault: 0,
    allowedTaxRates: [0],
    allowedLanguages: ["en"],
    taxIdType: "tax_id.global",
    placeholderIdType: "placeholder.global",
    validationRegex: /^[a-zA-Z0-9\s\-]{4,25}$/,
  },
};

/**
 * Time zones that can be selected by a clinic. Region defaults above remain
 * the source of truth when a clinic is created without a browser preference.
 */
export const CLINIC_TIMEZONES = Array.from(
  new Set([
    ...Object.values(REGIONAL_DICTIONARY).map((region) => region.timeZone),
    "America/New_York",
  ]),
);

export const getRegionalTimeZone = (
  countryCode?: string,
  fallback = REGIONAL_DICTIONARY.DEFAULT.timeZone,
) => {
  const key = countryCode?.trim().toUpperCase();
  return (key && REGIONAL_DICTIONARY[key]?.timeZone) || fallback;
};

export function normalizeTaxId(value: string): string {
  return value.toUpperCase().replace(/\s|-/g, "").trim();
}

export function validateRegionalTaxId(country: string, value: string): boolean {
  const region = REGIONAL_DICTIONARY[country] ?? REGIONAL_DICTIONARY.DEFAULT;

  const clean = normalizeTaxId(value);

  // 1) Validación por regex
  if (!region.validationRegex.test(clean)) {
    return false;
  }

  // 2) Validación avanzada (si existe)
  if (region.validateFunction) {
    return region.validateFunction(clean);
  }

  return true;
}
